import { useOptionalParticipationFlow } from "~/components/participation/ParticipationFlow";
import { rejectedCheckoutAttempt } from "./checkout-state";
import { DialogTitle } from "@headlessui/react";
import { Elements } from "@stripe/react-stripe-js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "~/hooks/use-user";
import { namedGroupSelection, canReplacePurchaseAttempt, groupTicketVariations } from "./group-checkout";
import { useCheckoutSession } from "./CheckoutSession";
import { sameCheckoutPurchase } from "./checkout-state";
import { useFetcher, useNavigate, useRevalidator, useSearchParams } from "react-router";
import {
  CreateEventTicketPurchaseIntentErrors,
  type EventFragment,
  type EventTicketPurchaserDetailsInput,
} from "~/__generated__/graphql/graphql";
import { PrimaryButton } from "~/components/Button";
import { StepIndicator } from "~/components/plans/components/StepIndicator";
import { Text } from "~/components/Text";
import type { PurchaseIntentResult } from "~/routes/api.events.$eventId.purchase-intent";
import type { TicketQuoteResult } from "~/routes/api.events.$eventId.ticket-quote";
import { useRootLocale } from "~/root";
import { trackLegacyCheckout as track } from "~/utils/analytics";
import { centsToMoney } from "~/utils/money";
import { getStripePromise } from "~/utils/stripe";
import { CheckoutSummaryCard } from "./CheckoutSummaryCard";
import { CheckoutFooter } from "./CheckoutFooter";

import { PurchaseForm, type PreparedPayment } from "./PurchaseForm";
import {
  getSelectedTicketVariation,
  TICKET_VARIATION_PARAM,
} from "./ticketVariations";

const STRIPE_ELEMENTS_APPEARANCE = {
  variables: {
    colorPrimary: "#F3564F",
    colorBackground: "#FFFFFF",
    colorText: "#4A5043",
    colorTextPlaceholder: "#C7C7CD",
    borderRadius: "9px",
    fontFamily: "Proxima-Nova, system-ui, sans-serif",
  },
  rules: {
    ".Label": {
      color: "#4A5043",
      fontFamily: "Proxima-Nova",
      fontWeight: "700",
      fontSize: "13.5px",
    },
    ".Tab": {
      borderColor: "#F5DCC4",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      color: "#4A5043",
    },
    ".Tab--selected": {
      borderColor: "#F5DCC4",
      backgroundColor: "rgba(245, 220, 196, 0.8)",
      color: "#4A5043",
    },
    ".Input": {
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "#F5DCC4",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      color: "#4A5043",
      fontFamily: "Proxima-Nova",
    },
    ".Input:focus": {
      borderColor: "#4A5043",
      boxShadow: "none",
    },
    ".Input--invalid": {
      borderColor: "#df1b41",
    },
  },
} as const;

const STRIPE_ELEMENTS_FONTS = [
  {
    family: "Proxima-Nova",
    src: "url(/Proxima-Nova-Regular.otf)",
    weight: "400",
  },
] as const;

type Props = {
  plan: EventFragment;
  authenticated: boolean;
  userExists: boolean;
  stripePublishableKey: string | undefined;
  ticketVariationsEnabled: boolean;
  purchaserDetails?: EventTicketPurchaserDetailsInput;
  onBack?: () => void;
};



export function CheckoutPaymentStep({
  plan,
  authenticated,
  userExists,
  stripePublishableKey,
  ticketVariationsEnabled,
  purchaserDetails,
  onBack,
}: Props) {
  const locale = useRootLocale();
  const { user } = useUser();
  const eventId = plan.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const revalidator = useRevalidator();
  const checkout = useCheckoutSession();
  const navigate = useNavigate();

  const { update: updateCheckout, flush: flushCheckout, canContinue } = checkout;
  const setPhase = useCallback((paymentPhase: "overview" | "method") => updateCheckout({ paymentPhase }), [updateCheckout]);
  const frozenAttempt = checkout.draft.editingPayment ? null : checkout.draft.attempt;
  const discountCode = frozenAttempt?.discountCode ?? searchParams.get("discountCode") ?? "";
  const requestedVariationID = frozenAttempt ? frozenAttempt.ticketVariationID : searchParams.get(TICKET_VARIATION_PARAM);
  const selectionPlan = checkout.draft.groupSlots.length > 1
    ? { ...plan, ticketVariations: groupTicketVariations(plan.ticketVariations, checkout.draft.groupSlots) }
    : plan;
  const selectedVariation =
    (ticketVariationsEnabled || frozenAttempt) && requestedVariationID
      ? getSelectedTicketVariation(selectionPlan, requestedVariationID)
      : undefined;
  const ticketVariationID = frozenAttempt ? frozenAttempt.ticketVariationID || undefined : selectedVariation?.id;
  const draftSelection = namedGroupSelection(checkout.draft.groupSlots, checkout.draft.purchaserSlotID);
  const additionalTickets = frozenAttempt?.group?.additionalTickets ?? (checkout.draft.groupSlots.length > 1 ? draftSelection?.additionalTickets : undefined);
  const isGroup = !!additionalTickets?.length;
  const ticketNoun = isGroup ? "tickets" : "ticket";
  const additionalTicketsKey = isGroup ? JSON.stringify(additionalTickets) : "";
  const groupNamesValid = checkout.draft.groupSlots.length <= 1 || !!draftSelection;
  const approvalCheckout = frozenAttempt?.approvalRequired ?? plan.approveGuests;
  const acceptedWaitlistClaim = !!purchaserDetails?.eventRequestID;
  const hostFirstName = plan.hosts.find(host => host.primary)?.user.firstName?.trim() || "the host";
  const approvalPaymentNotice = `Payment will be processed once ${hostFirstName} accepts.`;
  const serializedPurchaserDetails = useMemo(
    () => frozenAttempt?.purchaserDetails ?? (purchaserDetails ? JSON.stringify(purchaserDetails) : ""),
    [frozenAttempt?.purchaserDetails, purchaserDetails],
  );
  const [attemptLocked, setAttemptLocked] = useState(!checkout.draft.editingPayment && (!!checkout.draft.attempt || !!checkout.draft.walletIntentID));
  const recoveredIntentErrorRef = useRef<string | null>(null);
  const intentSubmissionRef = useRef<string | null>(null);
  const pendingPayment = useRef<((payment: PreparedPayment | null) => void) | null>(null);
  const flow = useOptionalParticipationFlow();
  const observe = flow?.observe;
  const preparation = useRef<ReturnType<NonNullable<typeof observe>>>(undefined);
  const [preparing, setPreparing] = useState(false);
  const [preparationError, setPreparationError] = useState<string | null>(null);
  const groupIntentScope = useMemo(() => crypto.randomUUID(), []);
  const intentKey = `checkout-intent:${checkout.scope}:${eventId}:${ticketVariationID ?? "full-access"}:${discountCode}:${serializedPurchaserDetails}:${groupIntentScope}`;
  const currentIntentKey = useRef(intentKey);
  currentIntentKey.current = intentKey;

  const quoteFetcher = useFetcher<TicketQuoteResult>({
    key: `ticket-quote:${eventId}`,
  });
  const intentFetcher = useFetcher<PurchaseIntentResult>({
    key: intentKey,
  });
  const stripePromise = useMemo(
    () =>
      stripePublishableKey ? getStripePromise(stripePublishableKey) : null,
    [stripePublishableKey],
  );

  const setStep = checkout.setStep;

  const goToTickets = () => setStep("tickets");
  const goToDetails = () => {
    if (acceptedWaitlistClaim) {
      goToTickets();
      return;
    }
    setStep(plan.formInputs.length || approvalCheckout ? "questions" : "details");
  };

  useEffect(() => {
    if (
      !frozenAttempt && ticketVariationsEnabled &&
      (!requestedVariationID || !ticketVariationID)
    ) {
      goToTickets();
      return;
    }
    if (!authenticated || !userExists) {
      setStep("details");
    }
    // `setStep` only updates checkout URL state and is intentionally local.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authenticated,
    requestedVariationID,
    ticketVariationID,
    ticketVariationsEnabled,
    frozenAttempt,
    userExists,
  ]);

  const loadQuote = quoteFetcher.load;
  const submitQuote = quoteFetcher.submit;
  useEffect(() => {
    if (ticketVariationsEnabled && !ticketVariationID) return;

    const params = new URLSearchParams();
    if (ticketVariationID) {
      params.set(TICKET_VARIATION_PARAM, ticketVariationID);
    }
    if (discountCode) params.set("discountCode", discountCode);
    if (!groupNamesValid && !frozenAttempt) return;
    if (additionalTicketsKey) submitQuote({ ticketVariationID: ticketVariationID!, additionalTickets: additionalTicketsKey, discountCode }, { method: "post", action: `/api/events/${eventId}/ticket-quote` });
    else loadQuote(`/api/events/${eventId}/ticket-quote?${params}`);
  }, [
    discountCode,
    additionalTicketsKey, groupNamesValid, frozenAttempt, submitQuote,
    eventId,
    loadQuote,
    ticketVariationID,
    ticketVariationsEnabled,
  ]);

  const quoteData = quoteFetcher.data;
  const quoteMatchesSelection =
    quoteData !== undefined &&
    quoteData.ticketVariationID === (ticketVariationID ?? null) &&
    quoteData.discountCode === discountCode && (quoteData.additionalTicketsKey ?? "") === additionalTicketsKey;
  const quote = quoteMatchesSelection && quoteData.ok ? quoteData.ticketCost : null;
  const quoteError =
    quoteFetcher.state === "idle" && quoteMatchesSelection
      ? quoteData.error
      : null;

  const intentData = intentFetcher.data;
  const clientSecret = intentData?.clientSecret ?? null;
  const walletIntentID = intentData?.walletIntentID ?? null;
  const intentCost = intentData?.ticketCost;
  const reviewQuote = frozenAttempt && (frozenAttempt.expectedTotalCents !== quote?.totalCostCents ||
    frozenAttempt.discountCode !== discountCode || frozenAttempt.ticketVariationID !== (ticketVariationID ?? ""))
    ? null : quote;
  const currentCost = intentCost ?? reviewQuote;
  const totalCents =
    intentCost?.totalCostCents ??
    frozenAttempt?.expectedTotalCents ??
    reviewQuote?.totalCostCents ??
    null;
  const originalTotalCents =
    intentCost?.originalTotalCents ?? reviewQuote?.originalTotalCents ?? null;
  const discountAmountCents =
    intentCost?.totalDiscountCents ?? reviewQuote?.totalDiscountCents ?? 0;

  useEffect(() => {
    if (!clientSecret) return;
    track("checkout_payment_viewed", {
      event_id: eventId,
      event_title: plan.title,
    });
  }, [clientSecret, eventId, plan.title]);


  useEffect(() => {
    if (intentFetcher.state !== "idle" || !intentData || !preparation.current) return;
    preparation.current.finish(intentData.ok ? "success" : canReplacePurchaseAttempt(intentData.errorType) ? "failure" : "unknown", intentData.ok ? undefined : "unavailable");
    preparation.current = undefined;
  }, [intentFetcher.state, intentData]);

  useEffect(() => {
    const errorType = intentData?.errorType;
    if (intentData?.ok === false) {
      setPreparationError(intentData.error ?? "We couldn't prepare payment. Please try again.");
    }
    if (errorType === CreateEventTicketPurchaseIntentErrors.ProfileIncomplete) {
      intentSubmissionRef.current = null;
      setAttemptLocked(false);
      updateCheckout(rejectedCheckoutAttempt(checkout.draft));
      if (recoveredIntentErrorRef.current === errorType) return;
      recoveredIntentErrorRef.current = errorType;
      checkout.setStep("profile", true);
      revalidator.revalidate();
      return;
    }
    if (errorType === "PRICE_CHANGED") {
      intentSubmissionRef.current = null;
      setAttemptLocked(false);
      updateCheckout(rejectedCheckoutAttempt(checkout.draft));
      setPhase("overview");
      const params = new URLSearchParams();
      if (ticketVariationID) params.set(TICKET_VARIATION_PARAM, ticketVariationID);
      if (discountCode) params.set("discountCode", discountCode);
      if (recoveredIntentErrorRef.current !== errorType) {
        recoveredIntentErrorRef.current = errorType;
        if (additionalTicketsKey) submitQuote({ ticketVariationID: ticketVariationID!, additionalTickets: additionalTicketsKey, discountCode }, { method: "post", action: `/api/events/${eventId}/ticket-quote` });
        else loadQuote(`/api/events/${eventId}/ticket-quote?${params}`);
      }
      return;
    }
    const invalidDiscount = errorType === "DISCOUNT_CODE_INVALID";
    const invalidQuote = errorType === "TICKET_QUOTE_INVALID";
    const unavailableVariation =
      errorType ===
      CreateEventTicketPurchaseIntentErrors.TicketVariationUnavailable;
    const fullEvent =
      errorType === CreateEventTicketPurchaseIntentErrors.EventFull;

    if (
      !invalidDiscount &&
      !invalidQuote &&
      !unavailableVariation &&
      !fullEvent
    ) {
      if (intentData?.ok === false) {
        intentSubmissionRef.current = null;
        if (canReplacePurchaseAttempt(errorType)) { setAttemptLocked(false); updateCheckout(rejectedCheckoutAttempt(checkout.draft)); }
        setPhase("overview");
      }
      return;
    }

    intentSubmissionRef.current = null;
    setAttemptLocked(false);
    updateCheckout(rejectedCheckoutAttempt(checkout.draft));
    const recoveryKey = `${errorType}:${ticketVariationID ?? "full-access"}:${discountCode}`;
    if (recoveredIntentErrorRef.current === recoveryKey) return;
    recoveredIntentErrorRef.current = recoveryKey;

    if (invalidDiscount || invalidQuote) {
      setPhase("overview");
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.delete("discountCode");
          return next;
        },
        { replace: true, preventScrollReset: true },
      );
      return;
    }

    checkout.setStep("tickets", true);
    revalidator.revalidate();
  }, [
    discountCode,
    intentData,
    additionalTicketsKey, submitQuote,
    revalidator,
    setSearchParams,
    ticketVariationID,
    eventId,
    loadQuote,
    setPhase,
    updateCheckout,
  ]);

  useEffect(() => {
    if (!intentData?.ok || !walletIntentID) return;
    updateCheckout({ walletIntentID, registrationID: intentData.registrationID ?? "", previousAttempt: null });
    if (!intentData.processing && (clientSecret || intentCost?.totalCostCents !== 0)) return;
    // Committed free or already-processing attempts reopen at the recovery
    // screen, never an editable form that could pay again.
    updateCheckout({ paymentPhase: "processing" });

    const processingURL = new URL(
      `/plans/${eventId}/purchase-processing`,
      window.location.origin,
    );
    processingURL.searchParams.set("walletIntentID", walletIntentID);
    let active = true;
    // The attempt ID is already in the destination URL. Draft storage must not strand
    // a committed free purchase or an in-flight payment on this screen.
    void flushCheckout().then(() => {
      if (active && canContinue()) navigate(processingURL.pathname + processingURL.search, { replace: true });
    });
    return () => { active = false; };
  }, [
    clientSecret,
    eventId,
    intentCost?.totalCostCents,
    intentData?.ok,
    intentData?.processing,
    intentData?.registrationID,
    walletIntentID,
    updateCheckout,
    flushCheckout,
    canContinue,
    navigate,
  ]);


  const quoteIsLoading =
    quoteFetcher.state !== "idle" ||
    !quoteMatchesSelection ||
    (!quote && !quoteError);
  const intentIsLoading = preparing || intentFetcher.state !== "idle";
  const paymentConfigurationError =
    totalCents !== null &&
    totalCents > 0 &&
    !stripePromise
      ? "Payment is not configured. Please try again later."
      : null;
  const displayedError = paymentConfigurationError ?? (intentIsLoading ? null
    : preparationError ?? intentData?.error ?? (!intentData?.ok ? quoteError : null));
  const showReview = totalCents === 0 || !!displayedError;
  const termsLocked = attemptLocked || !checkout.draft.editingPayment && (!!checkout.draft.attempt || !!checkout.draft.walletIntentID) || !!intentData?.ok;
  const canEdit = !intentIsLoading && checkout.draft.paymentPhase !== "processing" &&
    (!termsLocked || !!clientSecret && intentData?.ok === true && !intentData.processing);
  const editCheckout = (navigateBack: () => void) => {
    if (!canEdit) return;
    // Keep the accepted command for unchanged retries and ambiguous recovery.
    // The purchase endpoint replaces it only when a changed cart is submitted.
    updateCheckout({ editingPayment: true, paymentPhase: "overview" });
    navigateBack();
  };
  const createIntent = useCallback(async () => {
    const frozen = frozenAttempt;
    if ((!frozen && (!groupNamesValid || !quote?.available || quoteIsLoading || isGroup && !quoteData?.fingerprint)) || intentFetcher.state !== "idle") return false;
    if (intentData?.ok) {
      if (clientSecret) setPhase("method");
      return;
    }
    if (intentSubmissionRef.current === intentKey) return false;
    intentSubmissionRef.current = intentKey;
    recoveredIntentErrorRef.current = null;
    setPreparationError(null);
    const proposed = frozen ?? {
      expectedTotalCents: quote!.totalCostCents,
      discountCode,
      ticketVariationID: ticketVariationID ?? "",
      purchaserDetails: serializedPurchaserDetails,
      ...(isGroup ? { group: { additionalTickets: additionalTickets!, quoteFingerprint: quoteData!.fingerprint!, idempotencyKey: crypto.randomUUID() }, purchaserUserID: user!.id, approvalRequired: plan.approveGuests } : {}),
    };
    const terms = frozen ?? (sameCheckoutPurchase(checkout.draft.attempt, proposed) ? checkout.draft.attempt! : proposed);
    const previous = checkout.draft.previousAttempt ?? (checkout.draft.attempt || checkout.draft.walletIntentID ? {
      command: checkout.draft.attempt, walletIntentID: checkout.draft.walletIntentID, registrationID: checkout.draft.registrationID,
    } : null);
    const restorePrevious = () => updateCheckout({ attempt: previous?.command ?? null, walletIntentID: previous?.walletIntentID ?? "", registrationID: previous?.registrationID ?? "", previousAttempt: null, editingPayment: true });
    preparation.current = observe?.("prepare_payment");
    setPreparing(true);
    if (!frozen) updateCheckout({ attempt: terms, previousAttempt: previous, editingPayment: false, walletIntentID: "", registrationID: "" });
    const flushed = await flushCheckout();
    setPreparing(false);
    if (!frozen && currentIntentKey.current !== intentKey) { intentSubmissionRef.current = null; restorePrevious(); return false; }
    if (!flushed || !canContinue()) {
      preparation.current?.finish("failure", "session");
      intentSubmissionRef.current = null;
      if (!frozen) restorePrevious();
      if (!flushed) setPreparationError("We couldn’t save your checkout. Try again.");
      return false;
    }

    setAttemptLocked(true);
    if (terms.expectedTotalCents > 0) setPhase("method");
    const formData = new FormData();
    formData.append("expectedTotalCents", String(terms.expectedTotalCents));
    if (terms.discountCode) formData.append("discountCode", terms.discountCode);
    if (terms.ticketVariationID) {
      formData.append(TICKET_VARIATION_PARAM, terms.ticketVariationID);
    }
    if (terms.purchaserDetails) {
      formData.append("purchaserDetails", terms.purchaserDetails);
    }
    if (terms.group) { formData.append("group", JSON.stringify(terms.group)); formData.append("expectedUserID", terms.purchaserUserID ?? ""); }
    intentFetcher.submit(formData, {
      method: "post",
      action: `/api/events/${eventId}/purchase-intent`,
    });
    return true;
  }, [checkout, groupNamesValid, quote, quoteIsLoading, isGroup, quoteData, intentFetcher,
    intentData, clientSecret, setPhase, intentKey, discountCode, ticketVariationID,
    serializedPurchaserDetails, additionalTickets, user, plan.approveGuests,
    updateCheckout, flushCheckout, canContinue, eventId, frozenAttempt]);

  useEffect(() => {
    if (intentFetcher.state !== "idle" || !intentData || !pendingPayment.current) return;
    const resolve = pendingPayment.current;
    pendingPayment.current = null;
    resolve(intentData.ok && !intentData.processing && clientSecret && walletIntentID
      ? { clientSecret, walletIntentID } : null);
  }, [intentFetcher.state, intentData, clientSecret, walletIntentID]);

  const preparePayment = (): Promise<PreparedPayment | null> => {
    if (intentData?.ok && !intentData.processing && clientSecret && walletIntentID) {
      return Promise.resolve({ clientSecret, walletIntentID });
    }
    if (pendingPayment.current) return Promise.resolve(null);
    return new Promise(resolve => {
      pendingPayment.current = resolve;
      void createIntent().then(submitted => {
        if (!submitted && pendingPayment.current === resolve) {
          pendingPayment.current = null;
          resolve(null);
        }
      });
    });
  };

  useEffect(() => {
    // Only recover an already-submitted command on entry. New purchases remain
    // editable until Finalise (or wallet confirmation) explicitly submits them.
    if (frozenAttempt && authenticated && userExists &&
      totalCents !== null && totalCents > 0 && stripePromise &&
      !intentData && !intentIsLoading && !displayedError) {
      void createIntent();
    }
  }, [authenticated, userExists, totalCents, stripePromise,
    intentData, intentIsLoading, displayedError, frozenAttempt, createIntent]);

  const paymentSummary = (
    <>
      <CheckoutSummaryCard
        plan={plan}
        ticketVariation={selectedVariation}
        tickets={isGroup ? [{ eventTicketVariationID: ticketVariationID!, recipientName: "Yours" }, ...additionalTickets!] : undefined}
        ticketCosts={reviewQuote && reviewQuote.totalCostCents === currentCost?.totalCostCents &&
          (!frozenAttempt?.group || frozenAttempt.group.quoteFingerprint === quoteData?.fingerprint)
          ? quoteData?.ticketCosts : undefined}
        totalCostCents={totalCents}
        originalTotalCents={originalTotalCents}
        discountAmountCents={discountAmountCents}
        discountCode={discountCode || undefined}
        onEditTickets={canEdit ? () => editCheckout(goToTickets) : undefined}
      >
        {currentCost ? (
          <PriceBreakdown
            totalCents={currentCost.totalCostCents}
            discountAmountCents={currentCost.totalDiscountCents}
            originalTotalCents={currentCost.originalTotalCents}
            locale={locale}
          />
        ) : <PriceBreakdownSkeleton />}
      </CheckoutSummaryCard>
      <Text variant="primary" size="md" as="h2" className="mt-5">
        {totalCents === 0 ? `${approvalCheckout ? "Request" : "Confirm"} ${ticketNoun}` : "Payment"}
      </Text>
      {approvalCheckout && (
        <Text as="p" variant="secondary" size="sm" className="mt-4">
          {totalCents === 0 ? `The host will review your request before your ${ticketNoun} ${isGroup ? "are" : "is"} confirmed.` : approvalPaymentNotice}
        </Text>
      )}
    </>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col sm:block">
      <DialogTitle as="div" className="sr-only">
        {totalCents === 0 ? `Confirm ${ticketNoun}` : "Payment"}
      </DialogTitle>
      <div className="shrink-0">
        <StepIndicator
          currentStep={3}
          showTicketStep
          stepActions={canEdit ? { 1: () => editCheckout(goToTickets), 2: () => editCheckout(onBack ?? goToDetails) } : undefined}
        />
      </div>

      {showReview ? (
        <div className="mt-[10px] flex min-h-0 flex-1 flex-col sm:block">
          <div className="-mx-6 min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pt-[30px] pb-2 sm:mx-0 sm:overflow-visible sm:px-0 sm:pb-0">
            {paymentSummary}
            {totalCents === 0 && !approvalCheckout && (
              <Text size="sm" className="mt-2">Your {ticketNoun} {isGroup ? "are" : "is"} free. Confirm below to join the plan.</Text>
            )}
          </div>
          <CheckoutFooter onBack={() => editCheckout(onBack ?? goToDetails)} backDisabled={!canEdit} error={displayedError}>

            <PrimaryButton
              type="button"
              className="text-base!"
              loading={intentIsLoading}
              disabled={(!checkout.draft.attempt && (!groupNamesValid || !quote?.available || quoteIsLoading)) || intentIsLoading || !!paymentConfigurationError}
              onClick={createIntent}
            >
              {totalCents === 0
                ? approvalCheckout
                  ? "Send request"
                  : isGroup ? "Get tickets" : "Get ticket"
                : "Try again"}
            </PrimaryButton>

          </CheckoutFooter>
        </div>
      ) : (
        <div className="mt-[10px] flex min-h-0 flex-1 flex-col sm:block">
          {totalCents !== null && totalCents > 0 && currentCost?.currency && stripePromise && !intentData?.processing ? (
            <Elements
              stripe={stripePromise}
              options={{
                mode: "payment",
                amount: totalCents,
                currency: currentCost.currency.toLowerCase(),
                captureMethod: "manual",
                fonts: [...STRIPE_ELEMENTS_FONTS],
                appearance: STRIPE_ELEMENTS_APPEARANCE,
              }}
            >
              <PurchaseForm
                eventId={eventId}
                eventTitle={plan.title}
                preparePayment={preparePayment}
                totalCents={totalCents ?? 0}

                onBack={() => editCheckout(onBack ?? goToDetails)}
                backDisabled={!canEdit}
                error={displayedError}
              >
                {paymentSummary}
              </PurchaseForm>
            </Elements>
          ) : (
            <>
              <div className="-mx-6 min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pt-[30px] pb-2 sm:mx-0 sm:overflow-visible sm:px-0 sm:pb-0">
                {paymentSummary}
                {(intentIsLoading || !intentData) && (
                  <div className="mt-5"><PaymentFormSkeleton /></div>
                )}
              </div>
              <CheckoutFooter onBack={onBack ?? goToDetails} backDisabled>
                <PrimaryButton type="button" className="text-base!" disabled loading>
                  {intentData?.processing ? "Checking your ticket" : "Preparing payment"}
                </PrimaryButton>
              </CheckoutFooter>
            </>
          )}
        </div>
      )}
    </div>
  );
}

type PriceBreakdownProps = {
  totalCents: number;
  discountAmountCents: number;
  originalTotalCents: number | null;
  locale: string;
};

function PriceBreakdown({
  totalCents,
  discountAmountCents,
  originalTotalCents,
  locale,
}: PriceBreakdownProps) {
  const hasDiscount = discountAmountCents > 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="mt-1 flex items-center justify-between font-sans text-sm font-bold text-butter-green">
        <span>Total</span>
        <span className="inline-flex items-center gap-2">
          {hasDiscount && originalTotalCents !== null && (
            <span className="font-normal line-through opacity-60">
              {centsToMoney(originalTotalCents, "$", locale)}
            </span>
          )}
          <span>{centsToMoney(totalCents, "$", locale)}</span>
        </span>
      </div>
      {hasDiscount && (
        <p className="text-right font-sans text-sm font-normal text-butter-lime">
          You&apos;re saving {centsToMoney(discountAmountCents, "$", locale)}
        </p>
      )}
    </div>
  );
}

function PriceBreakdownSkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-label="Loading ticket price">
      <div className="flex h-6 items-center justify-between">
        <span className="h-5 w-16 animate-pulse rounded bg-butter-cream/70" />
        <span className="h-5 w-20 animate-pulse rounded bg-butter-cream/70" />
      </div>
    </div>
  );
}

function PaymentFormSkeleton() {
  return (
    <div className="flex flex-col gap-3" role="status" aria-label="Loading payment form">
      <div className="h-12 animate-pulse rounded-[9px] bg-butter-cream/70" />
      <div className="rounded-xl bg-white/60 p-4 sm:p-5">
        <div className="flex flex-col gap-3">
          <div className="h-12 animate-pulse rounded-[9px] bg-butter-cream/70" />
          <div className="flex gap-3">
            <div className="h-12 flex-1 animate-pulse rounded-[9px] bg-butter-cream/70" />
            <div className="h-12 flex-1 animate-pulse rounded-[9px] bg-butter-cream/70" />
          </div>
          <div className="h-12 animate-pulse rounded-[9px] bg-butter-cream/70" />
        </div>
      </div>

    </div>
  );
}
